// Registrar overview across every church at /portal/health/admin/overview.
// Aggregate only — one row per cycle with participation and a safeguarding
// signal, plus roll-up tiles. Never a person, never an answer: the same
// anonymity wall as everywhere else in the module.

import { useEffect, useState, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { healthApi, HealthApiError, type CrossResponse, type CrossCycleRow } from './api';
import { BAND_LABELS, CYCLE_STATUS_LABELS, formatNzDateShort } from './format';

const BAND_COLOR: Record<string, string> = {
  embedded: 'var(--band-embedded)',
  strong: 'var(--band-strong)',
  developing: 'var(--band-developing)',
  priority: 'var(--band-priority)',
};

const SAFEGUARD: Record<CrossCycleRow['safeguarding'], { label: string; color: string }> = {
  concern: { label: 'Safeguarding concern', color: 'var(--band-priority)' },
  watch: { label: 'Watch', color: 'var(--band-developing)' },
  ok: { label: 'No flags', color: 'var(--band-embedded)' },
  pending: { label: 'Awaiting responses', color: 'var(--health-grey)' },
};

// Registrar attention order: concerns first, then watch, then the rest.
const SAFEGUARD_RANK: Record<CrossCycleRow['safeguarding'], number> = {
  concern: 0,
  watch: 1,
  ok: 2,
  pending: 3,
};

function Pill({ color, children }: { color: string; children: string }): JSX.Element {
  return (
    <span
      style={{
        display: 'inline-block',
        background: color,
        color: '#fff',
        fontSize: 11.5,
        fontWeight: 700,
        padding: '2px 9px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

function Tile({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: string;
}): JSX.Element {
  return (
    <div className="card" style={{ padding: '12px 16px', minWidth: 120, flex: '1 1 120px' }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent ?? 'inherit', lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: 'var(--health-grey)', marginTop: 2 }}>{label}</div>
    </div>
  );
}

export function CrossChurchPage(): JSX.Element {
  const navigate = useNavigate();
  const [data, setData] = useState<CrossResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    healthApi
      .cross()
      .then(setData)
      .catch((err: unknown) => {
        if (err instanceof HealthApiError && err.status === 401) {
          navigate('/auth/login?returnTo=%2Fportal%2Fhealth%2Fadmin%2Foverview', { replace: true });
          return;
        }
        setError(
          err instanceof HealthApiError && err.status === 403
            ? 'This overview is only open to the Registrar.'
            : 'The overview could not be loaded. Please try again.',
        );
      });
  }, [navigate]);

  if (error) {
    return (
      <div className="card" style={{ maxWidth: 560, margin: '30px auto', textAlign: 'center' }}>
        <p style={{ fontSize: 14, color: 'var(--health-grey)', lineHeight: 1.7 }}>{error}</p>
      </div>
    );
  }
  if (!data) return <p style={{ color: 'var(--health-grey)' }}>Loading overview...</p>;

  const rows = [...data.cycles].sort(
    (a, b) =>
      SAFEGUARD_RANK[a.safeguarding] - SAFEGUARD_RANK[b.safeguarding] ||
      a.church_name.localeCompare(b.church_name),
  );

  return (
    <div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>Across all churches</h1>
      <p style={{ fontSize: 13.5, color: 'var(--health-grey)', marginBottom: 18 }}>
        Every assessment at a glance. Figures are anonymous aggregates; open a church to see its
        full picture.
      </p>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <Tile label="Churches" value={data.summary.churches} />
        <Tile label="Open" value={data.summary.open} />
        <Tile label="Closed" value={data.summary.closed} />
        <Tile
          label="Safeguarding concerns"
          value={data.summary.safeguarding_concern}
          accent={data.summary.safeguarding_concern > 0 ? 'var(--band-priority)' : 'inherit'}
        />
        <Tile
          label="Watch"
          value={data.summary.safeguarding_watch}
          accent={data.summary.safeguarding_watch > 0 ? 'var(--band-developing)' : 'inherit'}
        />
      </div>

      {rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
          <p style={{ fontSize: 14.5 }}>No assessments yet.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: '8px 16px 4px' }}>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Church</th>
                  <th scope="col">Status</th>
                  <th scope="col">Window</th>
                  <th scope="col">Responses</th>
                  <th scope="col">Overall</th>
                  <th scope="col">Safeguarding</th>
                  <th scope="col" aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {rows.map((cy) => {
                  const window =
                    [formatNzDateShort(cy.opens_at), formatNzDateShort(cy.closes_at)]
                      .filter(Boolean)
                      .join(' to ') || 'Not set';
                  const sg = SAFEGUARD[cy.safeguarding];
                  return (
                    <tr key={cy.id}>
                      <td style={{ fontWeight: 600 }}>
                        <Link to={`/portal/health/${cy.id}`}>{cy.church_name}</Link>
                      </td>
                      <td>{CYCLE_STATUS_LABELS[cy.status] ?? cy.status}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{window}</td>
                      <td>
                        {cy.submitted}
                        {cy.invited > 0 ? ` / ${cy.invited}` : ''}
                        {cy.completion_pct !== null ? (
                          <span style={{ color: 'var(--health-grey)' }}>
                            {' '}
                            ({cy.completion_pct}%)
                          </span>
                        ) : null}
                      </td>
                      <td>
                        {cy.overall_band ? (
                          <Pill color={BAND_COLOR[cy.overall_band] ?? 'var(--health-grey)'}>
                            {`${BAND_LABELS[cy.overall_band]}${cy.overall_mean !== null ? ` · ${cy.overall_mean.toFixed(1)}` : ''}`}
                          </Pill>
                        ) : (
                          <span style={{ color: 'var(--health-grey)' }}>—</span>
                        )}
                      </td>
                      <td>
                        <Pill color={sg.color}>{sg.label}</Pill>
                      </td>
                      <td>
                        <Link to={`/portal/health/${cy.id}`} className="btn btn-ghost btn-sm">
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
